import {
  currencyRateConversion,
  formatDateLocal,
  formatAmount,
} from "../../../../utils/hooks/dataHandler";
import { generateMainCompPdf } from "../../../MainTaxComputation/Pdf/generateMainCompPdf";
import { calculateIrasNonSgdReferenceValues } from "../../../MainTaxComputation/Pdf/iras/sgdCalculation";

export const PL_FLIP_DEFAULT = {
  Revenue: "positive",
  "Other Income": "positive",
  "Cost of Sales": "negative",
  Expenses: "negative",
};

export const sectionSignedTotal = (rows, flipMode, sectionLabel) => {
  const mode = flipMode || PL_FLIP_DEFAULT;
  const isNegative = mode[sectionLabel] === "negative";
  return (rows || []).reduce((sum, row) => {
    const amount = parseFloat(row.amount);
    const valid = !isNaN(amount) ? amount : 0;
    return isNegative ? sum - valid : sum + valid;
  }, 0);
};

export const derivePlFigures = (pl) => {
  if (!pl) return null;
  const flip = pl.flipSignMode || PL_FLIP_DEFAULT;
  const tRev = sectionSignedTotal(pl.Revenue, flip, "Revenue");
  const tOi = sectionSignedTotal(pl.OtherIncome, flip, "Other Income");
  const tCos = sectionSignedTotal(pl.CostOfSales, flip, "Cost of Sales");
  const tExp = sectionSignedTotal(pl.Expenses, flip, "Expenses");
  const npbt = tRev + tOi + tCos + tExp;
  const revenueDisplay = tRev + tOi;
  const totalExpensesDisplay = revenueDisplay - npbt;
  return {
    npbt,
    revenueDisplay,
    totalExpensesDisplay,
    currency: pl.currency || "SGD",
  };
};

const normalizeCurrency = (currency) =>
  String(currency || "SGD")
    .trim()
    .toUpperCase();

const parsePositiveRate = (...rates) => {
  for (const rate of rates) {
    const parsed = parseFloat(rate);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 1;
};

export const getReferenceCurrencyMeta = (
  parsedMainComp,
  plSnapshot,
  selectedTc
) => {
  const currency = normalizeCurrency(
    parsedMainComp?.currency ||
      parsedMainComp?.company?.currency ||
      plSnapshot?.currency ||
      selectedTc?.currency ||
      "SGD"
  );

  const currencyRate = parsePositiveRate(
    parsedMainComp?.currencyRate,
    parsedMainComp?.company?.manual_currency_rate,
    parsedMainComp?.company?.currencyRate
  );

  return {
    currency,
    currencyRate,
    isForeignCurrency: currency !== "SGD",
  };
};

export const convertReferenceAmountToSgd = (amount, currencyMeta) => {
  const value = parseFloat(amount);
  if (!Number.isFinite(value)) return amount;
  if (!currencyMeta?.isForeignCurrency) return value;
  return currencyRateConversion(value, currencyMeta.currencyRate, "SGD");
};

export const getNonSgdReferenceValues = (parsed, currencyMeta) => {
  if (
    !parsed ||
    !currencyMeta?.isForeignCurrency ||
    parsed.company?.companyType === "investment" ||
    parsed.company?.taxPeriodType === "cross_ya"
  ) {
    return null;
  }

  return calculateIrasNonSgdReferenceValues({
    corporateIncomeTaxRebate: parsed.corporateIncomeTaxRebate,
    currencyRate: currencyMeta.currencyRate,
    currentYearRebate: parsed.currentYearRebate,
    isStartup: parsed.isStartup,
    netProfitBeforeTax: parsed.netProfitBeforeTax,
    referenceData: parsed.referenceData,
    taxPreviouslyAssessed: parsed.taxPreviouslyAssessed,
    unutilisedCapitalAllowances: parsed.unutilisedCapitalAllowances,
    unutilisedDonations: parsed.unutilisedDonations,
    unutilisedLosses: parsed.unutilisedLosses,
  });
};

export const parseComputationDocBlob = (raw) => {
  if (raw == null || raw === "") return null;
  try {
    const p = typeof raw === "string" ? JSON.parse(raw) : raw;
    return p && typeof p === "object" ? p : null;
  } catch {
    return null;
  }
};

export const formatMainCompSavedAt = (iso) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("en-SG", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return null;
  }
};

export const pickDefaultTaxComputation = (list) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  const currentYear = new Date().getFullYear();
  const match = list.find((x) => Number(x.yearOfAssessment) === currentYear);
  if (match) return match;
  return [...list].sort(
    (a, b) => Number(b.yearOfAssessment) - Number(a.yearOfAssessment)
  )[0];
};

export const buildReferenceSummary = (parsed, plSnapshot, selectedTc) => {
  if (!parsed) return null;
  const cs = parsed.computedSummary || {};
  const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };
  const plf = derivePlFigures(plSnapshot);
  const currencyMeta = getReferenceCurrencyMeta(parsed, plSnapshot, selectedTc);
  const nonSgdReference = getNonSgdReferenceValues(parsed, currencyMeta);
  const nonSgdTaxSummary = nonSgdReference?.taxSummary;
  const npbt = num(parsed.netProfitBeforeTax ?? plf?.npbt);
  return {
    npbt,
    npbtSgd: nonSgdReference?.sectionValues.net_profit_before_tax,
    revenue: plf?.revenueDisplay,
    totalExpenses: plf?.totalExpensesDisplay,
    currency: currencyMeta.currency,
    currencyRate: currencyMeta.currencyRate,
    isForeignCurrency: currencyMeta.isForeignCurrency,
    chargebleIncomeBeforeExempt: num(
      nonSgdTaxSummary?.chargeableIncomeBeforeExemptAmount ??
        cs.chargebleIncomeBeforeExemptAmount
    ),
    chargebleIncomeAfterExempt: num(
      nonSgdTaxSummary?.chargeableIncomeAfterExemptAmount ??
        cs.chargebleIncomeAfterExemptAmount
    ),
    taxPayableAt17: num(
      nonSgdTaxSummary?.taxableIncome ?? cs.taxableIncome
    ),
    postDeductions: num(
      nonSgdTaxSummary?.postDeductions ?? cs.postDeductions
    ),
    netTaxPayable: num(
      nonSgdTaxSummary?.netTaxPayable ?? cs.netTaxPayable
    ),
    isStartup: Boolean(parsed.isStartup),
  };
};

const buildMainCompCompanyForPdf = (mainCompCompany, selectedTc) => ({
  ...(selectedTc || {}),
  ...(mainCompCompany || {}),
  financialYearEnd:
    selectedTc?.financialYearEnd ?? mainCompCompany?.financialYearEnd,
  financialYearStart:
    selectedTc?.financialYearStart ?? mainCompCompany?.financialYearStart,
  taxPeriodType: selectedTc?.taxPeriodType ?? mainCompCompany?.taxPeriodType,
  tc_id: selectedTc?.tc_id ?? mainCompCompany?.tc_id,
  yearOfAssessment:
    selectedTc?.yearOfAssessment ?? mainCompCompany?.yearOfAssessment,
});

export const parseMainCompDocForPdf = async (mainCompDoc, selectedTc = null) => {
  if (mainCompDoc == null || mainCompDoc === "") {
    return { error: "No saved Main Tax Computation snapshot yet." };
  }
  let parsed;
  try {
    parsed =
      typeof mainCompDoc === "string" ? JSON.parse(mainCompDoc) : mainCompDoc;
  } catch {
    return { error: "Saved Main Tax Computation data could not be read." };
  }
  if (!parsed || typeof parsed !== "object" || !parsed.company) {
    return {
      error:
        "Nothing printable yet. Save the Main Tax Computation in the Tax Computation tab first.",
    };
  }
  const {
    company: mcCompany,
    currency,
    currencyRate,
    netProfitBeforeTax,
    referenceData,
    unutilisedCapitalAllowances,
    unutilisedLosses,
    currentYearLosses,
    unutilisedDonations,
    corporateIncomeTaxRebate,
    currentYearRebate,
    isStartup,
    taxPreviouslyAssessed,
    computedSummary,
    unabsorbedSummary,
    crossYAApportionmentContext,
    comments,
    reviewNote,
  } = parsed;
  const printableCompany = buildMainCompCompanyForPdf(mcCompany, selectedTc);
  try {
    const doc = await Promise.resolve(
      generateMainCompPdf(
        false,
        printableCompany,
        currency,
        currencyRate,
        netProfitBeforeTax ?? 0,
        referenceData,
        unutilisedCapitalAllowances,
        unutilisedLosses,
        currentYearLosses,
        unutilisedDonations,
        corporateIncomeTaxRebate,
        currentYearRebate,
        isStartup,
        taxPreviouslyAssessed,
        computedSummary,
        unabsorbedSummary ?? { caRows: [], lossRows: [], donationRows: [] },
        crossYAApportionmentContext,
        comments,
        reviewNote
      )
    );
    return { doc };
  } catch (e) {
    return { error: e?.message || String(e) };
  }
};

export const formatTcOptionLabel = (tc) => {
  let fye = "";
  try {
    if (tc.financialYearEnd) {
      fye = formatDateLocal(new Date(tc.financialYearEnd));
    }
  } catch {
    fye = "";
  }
  const ya = tc.yearOfAssessment ?? "—";
  const cur = tc.currency || "SGD";
  return fye ? `YA ${ya} · FYE ${fye} · ${cur}` : `YA ${ya} · ${cur}`;
};

export const formatRefAmount = (currency, value) => {
  if (value == null || Number.isNaN(value)) return "—";
  const cur = currency || "SGD";
  return `${cur} ${formatAmount(value)}`;
};
